/**
 * Service to validate if a Google Place should be saved as a camp/youth program
 * Prevents storing personal addresses and irrelevant locations
 */

// Valid Google Places types for youth programs/camps
const VALID_PLACE_TYPES = [
  'point_of_interest',
  'establishment',
  'school',
  'university',
  'secondary_school',
  'primary_school',
  'tourist_attraction',
  'campground',
  'lodging',
  'park',
  'stadium',
  'gym',
  'health',
  'sports_complex',
  'community_center',
  'museum',
  'art_gallery',
  'aquarium',
  'zoo',
  'amusement_park'
];

// Types that definitely indicate NOT a youth program
const INVALID_PLACE_TYPES = [
  'street_address',
  'route',
  'premise',
  'subpremise',
  'postal_code',
  'neighborhood',
  'locality',
  'administrative_area_level_1',
  'administrative_area_level_2',
  'country',
  'political',
  'restaurant',
  'cafe',
  'bar',
  'store',
  'shopping_mall',
  'parking',
  'gas_station',
  'airport',
  'transit_station'
];

// Keywords that indicate a youth program in the name or description
const YOUTH_PROGRAM_KEYWORDS = [
  'camp',
  'summer camp',
  'day camp',
  'overnight camp',
  'sleepaway camp',
  'kids',
  'children',
  'youth',
  'junior',
  'academy',
  'program',
  'enrichment',
  'learning center',
  'education',
  'afterschool',
  'childcare',
  'daycare',
  'preschool',
  'sports camp',
  'art camp',
  'science camp',
  'tech camp',
  'coding camp',
  'music camp',
  'dance camp',
  'gymnastics',
  'martial arts',
  'swimming lessons',
  'tutoring',
  'ymca',
  'ywca',
  'boys and girls club',
  'recreation center'
];

/**
 * Validates if a Google Place should be saved to the database
 * @param {Object} placeData - Google Places API response data
 * @returns {Object} { isValid: boolean, reason: string, confidence: number }
 */
export const validateYouthProgram = (placeData) => {
  console.group('🔍 Validating if place is a youth program');
  console.log('Place:', placeData.name);
  
  const result = {
    isValid: false,
    reason: '',
    confidence: 0 // 0-100 score
  };

  // Extract relevant data - handle multiple name field formats
  const name = (
    placeData.name || 
    placeData.displayName?.text || 
    placeData.structured_formatting?.main_text || 
    ''
  ).toLowerCase();
  const types = placeData.types || [];
  const businessStatus = placeData.businessStatus || placeData.business_status;
  const description = (placeData.editorial_summary?.text || placeData.editorialSummary?.text || '').toLowerCase();
  const website = placeData.website || '';
  const phoneNumber = placeData.phoneNumber || placeData.phone || '';

  // Rule 1: Reject if it's clearly an address/location type
  const hasInvalidType = types.some(type => INVALID_PLACE_TYPES.includes(type));
  if (hasInvalidType && !types.some(type => VALID_PLACE_TYPES.includes(type))) {
    result.reason = 'Location type indicates personal/residential address';
    console.log('❌ INVALID - Address type detected');
    console.groupEnd();
    return result;
  }

  // Rule 2: Reject if no name (likely just an address)
  if (!name || name.length < 3) {
    result.reason = 'No business name - likely just an address';
    console.log('❌ INVALID - No business name');
    console.groupEnd();
    return result;
  }

  // Rule 3: Check for youth program keywords in name
  const nameMatchesKeywords = YOUTH_PROGRAM_KEYWORDS.some(keyword => 
    name.includes(keyword.toLowerCase())
  );
  
  if (nameMatchesKeywords) {
    result.isValid = true;
    result.confidence = 85;
    result.reason = 'Name contains youth program keywords';
    console.log('✅ VALID - Youth program keywords in name');
    console.groupEnd();
    return result;
  }

  // Rule 4: Check description for keywords
  if (description) {
    const descriptionMatchesKeywords = YOUTH_PROGRAM_KEYWORDS.some(keyword => 
      description.includes(keyword.toLowerCase())
    );
    
    if (descriptionMatchesKeywords) {
      result.isValid = true;
      result.confidence = 75;
      result.reason = 'Description contains youth program keywords';
      console.log('✅ VALID - Youth program keywords in description');
      console.groupEnd();
      return result;
    }
  }

  // Rule 5: Has valid place type + business indicators (website/phone)
  const hasValidType = types.some(type => VALID_PLACE_TYPES.includes(type));
  const hasBusinessIndicators = website || phoneNumber;
  
  if (hasValidType && hasBusinessIndicators && businessStatus === 'OPERATIONAL') {
    result.isValid = true;
    result.confidence = 60;
    result.reason = 'Valid place type with business indicators';
    console.log('✅ VALID - Business type with indicators');
    console.groupEnd();
    return result;
  }

  // Rule 6: Check if place has reviews and rating (businesses have these, addresses don't)
  const hasReviews = (placeData.userRatingCount || placeData.user_ratings_total || 0) > 0;
  const hasRating = (placeData.rating || 0) > 0;
  
  if (hasReviews && hasRating && hasValidType) {
    result.isValid = true;
    result.confidence = 70;
    result.reason = 'Has reviews and ratings - likely a business';
    console.log('✅ VALID - Has reviews/ratings');
    console.groupEnd();
    return result;
  }

  // Default: Reject if none of the above rules pass
  result.reason = 'Does not match youth program criteria';
  console.log('❌ INVALID - No matching criteria');
  console.groupEnd();
  return result;
};

/**
 * Batch validate multiple places
 * @param {Array} places - Array of Google Places data
 * @returns {Array} Valid places with validation metadata
 */
export const batchValidateYouthPrograms = (places) => {
  console.log(`🔍 Batch validating ${places.length} places...`);
  
  const validPlaces = places
    .map(place => {
      const validation = validateYouthProgram(place);
      return {
        ...place,
        validation
      };
    })
    .filter(place => place.validation.isValid);

  console.log(`✅ ${validPlaces.length}/${places.length} places passed validation`);
  
  return validPlaces;
};

/**
 * Get validation statistics for debugging
 * @param {Array} places - Array of Google Places data
 * @returns {Object} Statistics about validation results
 */
export const getValidationStats = (places) => {
  const results = places.map(place => validateYouthProgram(place));
  
  return {
    total: places.length,
    valid: results.filter(r => r.isValid).length,
    invalid: results.filter(r => !r.isValid).length,
    reasons: results.reduce((acc, r) => {
      acc[r.reason] = (acc[r.reason] || 0) + 1;
      return acc;
    }, {}),
    avgConfidence: results
      .filter(r => r.isValid)
      .reduce((sum, r) => sum + r.confidence, 0) / results.filter(r => r.isValid).length || 0
  };
};
